import { Form, Formik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createStandardVintage, listStandards } from "@/features/reference/api";
import { ApiError } from "@/lib/api";

const MONEY_RE = /^\d+(\.\d{1,2})?$/;
const money = () => Yup.string().matches(MONEY_RE, "Enter a valid amount").required("Required");

export function OtherAllowancesTab({
  effectiveOn,
  reloadToken,
}: {
  effectiveOn: string;
  reloadToken: number;
}) {
  const [under65, setUnder65] = useState("—");
  const [over65, setOver65] = useState("—");
  const [asset, setAsset] = useState("—");
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const refetch = useCallback(() => {
    setLoading(true);
    Promise.all([
      listStandards({ standardType: "HEALTHCARE_OOP", effectiveOn }),
      listStandards({ standardType: "ASSET_EXEMPTION", effectiveOn }),
    ])
      .then(([oop, ax]) => {
        setUnder65(oop.items.find((s) => s.item === "under_65")?.monthlyAmount ?? "—");
        setOver65(oop.items.find((s) => s.item === "over_65")?.monthlyAmount ?? "—");
        setAsset(ax.items[0]?.monthlyAmount ?? "—");
      })
      .catch(() => toast.error("Couldn't load other allowances."))
      .finally(() => setLoading(false));
  }, [effectiveOn]);

  // reloadToken is a deliberate extra trigger (e.g. after a JSON import).
  useEffect(() => {
    refetch();
  }, [refetch, reloadToken]);

  if (loading) return <p className="text-muted-foreground text-sm">Loading…</p>;

  return (
    <div className="max-w-md">
      <div className="rounded-lg border p-4">
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Out-of-pocket health · under 65</dt>
          <dd className="text-right tabular-nums">{under65}</dd>
          <dt className="text-muted-foreground">Out-of-pocket health · 65 and older</dt>
          <dd className="text-right tabular-nums">{over65}</dd>
          <dt className="text-muted-foreground">Personal-asset (exemption) deduction</dt>
          <dd className="text-right tabular-nums">{asset}</dd>
        </dl>
        <Button size="sm" variant="outline" className="mt-4" onClick={() => setEditOpen(true)}>
          New vintage
        </Button>
      </div>
      {editOpen && (
        <EditDialog
          initial={{ under65, over65, asset }}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function EditDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: { under65: string; over65: string; asset: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  const clean = (v: string) => (v === "—" ? "" : v);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New other-allowance vintage</DialogTitle>
          <DialogDescription>OOP health and personal-asset deduction, at a new effective date.</DialogDescription>
        </DialogHeader>
        <Formik
          initialValues={{
            effectiveDate: new Date().toISOString().slice(0, 10),
            under65: clean(initial.under65),
            over65: clean(initial.over65),
            asset: clean(initial.asset),
          }}
          validationSchema={Yup.object({
            effectiveDate: Yup.string().required("Required"),
            under65: money(),
            over65: money(),
            asset: money(),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await createStandardVintage(values.effectiveDate, [
                { standardType: "HEALTHCARE_OOP", item: "under_65", householdSize: null, monthlyAmount: values.under65 },
                { standardType: "HEALTHCARE_OOP", item: "over_65", householdSize: null, monthlyAmount: values.over65 },
                { standardType: "ASSET_EXEMPTION", item: "individual", householdSize: null, monthlyAmount: values.asset },
              ]);
              toast.success("New allowance vintage saved");
              onSaved();
            } catch (err) {
              toast.error(err instanceof ApiError ? err.message : "Couldn't save the vintage.");
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-4">
              <FormField name="effectiveDate" label="Effective date" type="date" />
              <FormField name="under65" label="OOP health · under 65" inputMode="decimal" />
              <FormField name="over65" label="OOP health · 65 and older" inputMode="decimal" />
              <FormField name="asset" label="Personal-asset deduction" inputMode="decimal" />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save vintage"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
